/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import * as _ from 'lodash';

import template from './context-service-configuration-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {IAB_TAXONOMY} from '../../../components/util/iab-taxonomy';
import {WINDOW} from '../../angular-platform-providers';
import {
  ContextServiceConfigurationApiService,
  ContextServiceConfigurationModel
} from './context-service-configuration-api.service';
import {escapeCsvValue} from '../../common/util/csv-value';
import {AppSharedModule} from '../../common/app-shared.module';
import {ReferenceDataUploadComponent} from '../reference-data/reference-data-upload.component';

@Component({
  standalone: true,
  selector: 'adn-context-service-configuration-edit-page',
  imports: [AppSharedModule, FormsModule, TranslateModule, ReferenceDataUploadComponent],
  template
})
export class ContextServiceConfigurationEditPageComponent
  extends SignalAdminResourceEditPageBase<ContextServiceConfigurationModel> {
  iabTaxonomy: any[] = _.cloneDeep(IAB_TAXONOMY);
  vm: any = {
    keywordMetaTags: '',
    categoryMetaTags: '',
    deriveKeywordsFromUrl: false,
    categoryKeywords: [],
    selectorConfiguration: [],
    trackedKeywords: []
  };
  detailsPref = false;
  readonly isLoading = signal(false);

  private readonly contextApi: ContextServiceConfigurationApiService;

  constructor(
    @Inject(WINDOW) private readonly window: any,
    private readonly ngZone: NgZone
  ) {
    const api = inject(ContextServiceConfigurationApiService);
    super(api, 'contextServiceConfiguration');
    this.contextApi = api;
  }

  get visibleTrackedKeywords(): any[] {
    return _.filter(this.vm.trackedKeywords || [], (trackedKeyword) => !trackedKeyword.deleted);
  }

  protected override initialiseModel(model: ContextServiceConfigurationModel): ContextServiceConfigurationModel {
    const initialised = super.initialiseModel(model);
    this.hydrateVm(initialised);
    return initialised;
  }

  protected override afterSave(model: ContextServiceConfigurationModel): void {
    this.hydrateVm(model);
  }

  protected override prepareForSubmit(model: ContextServiceConfigurationModel): void {
    model.teamMappings = _.map(_.keyBy(_.filter(model.teamMappings, (tm) => {
      return tm.team && tm.team.id && _.trim(tm.siteGroup).length > 0;
    }), 'team.id'), (tm) => tm);

    model.crawlerConfig = model.crawlerConfig || {};
    model.crawlerConfig.keywordMetaTags = _.isString(this.vm.keywordMetaTags)
      ? this.vm.keywordMetaTags.split(',').map((tag: string) => tag.trim())
      : [];
    model.crawlerConfig.categoryMetaTags = _.isString(this.vm.categoryMetaTags)
      ? this.vm.categoryMetaTags.split(',').map((tag: string) => tag.trim())
      : [];
    model.crawlerConfig.deriveKeywordsFromUrl = _.isBoolean(this.vm.deriveKeywordsFromUrl)
      ? this.vm.deriveKeywordsFromUrl
      : false;
    model.crawlerConfig.categoryKeywords = {};
    model.crawlerConfig.trackedKeywords = _.isArray(this.vm.trackedKeywords)
      ? this.vm.trackedKeywords
        .filter((trackedKeyword: any) => !(trackedKeyword.deleted || false))
        .filter((trackedKeyword: any) => trackedKeyword.keyword.trim().length)
        .map((trackedKeyword: any) => this.sanitiseVmTrackedKeywords(trackedKeyword))
      : [];
    (this.vm.categoryKeywords || []).forEach((categoryKeyword: any) => {
      if (categoryKeyword.category) {
        model.crawlerConfig.categoryKeywords[categoryKeyword.category] = (categoryKeyword.keywords || '')
          .split(',').map((keyword: string) => keyword.trim()).filter((keyword: string) => keyword.length > 0);
      }
    });
    model.crawlerConfig.selectorConfiguration = {};
    (this.vm.selectorConfiguration || []).forEach((selector: any) => {
      if (selector.domain) {
        if (selector.value?.isJavaScript) {
          delete selector.value.excludeSelector;
        }
        model.crawlerConfig.selectorConfiguration[selector.domain] = (selector.value || {});
      }
    });
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  toggleKeyword(keyword: any): void {
    keyword.showTextArea = !keyword.showTextArea;
    keyword.showTextAreaRows = Math.max(parseInt(String(keyword.keywords.length / 150), 10), 4);
  }

  addIabCategory(): void {
    this.vm.categoryKeywords = this.vm.categoryKeywords || [];
    this.vm.categoryKeywords.push({category: '', keywords: ''});
    this.markExternalFormDirty();
  }

  deleteIabCategory(index: number): void {
    _.pullAt(this.vm.categoryKeywords, index);
    this.markExternalFormDirty();
  }

  addSelector(): void {
    this.vm.selectorConfiguration = this.vm.selectorConfiguration || [];
    this.vm.selectorConfiguration.push({domain: '', value: {isJavaScript: false}});
    this.markExternalFormDirty();
  }

  deleteSelector(index: number): void {
    _.pullAt(this.vm.selectorConfiguration, index);
    this.markExternalFormDirty();
  }

  toggleEditTrackedKeyword(index: number): void {
    _.forEach(this.vm.trackedKeywords, (trackedKeyword: any, i: any) => {
      trackedKeyword.edited = Number(i) === index ? !trackedKeyword.edited : false;
    });
    this.markExternalFormDirty();
  }

  addTrackedKeyword(): void {
    this.vm.trackedKeywords = this.vm.trackedKeywords || [];
    this.vm.trackedKeywords.unshift({
      keyword: '',
      mappings: '',
      edited: true
    });
    this.markExternalFormDirty();
  }

  removeTrackedKeyword(trackedKeyword: any): void {
    this.markExternalFormDirty();
    trackedKeyword.edited = false;
    trackedKeyword.deleted = true;
  }

  downloadTrackedKeywordsCsv(): void {
    const rows = this.getRows();
    const header = 'keyword,mappings';
    const body = rows.map((row) => {
      const mappings = _.isArray(row.mappings) ? row.mappings.join(',') : (row.mappings || '');
      return [escapeCsvValue(row.keyword), escapeCsvValue(mappings)].join(',');
    }).join('\r\n');
    const csv = '\uFEFF' + header + '\r\n' + body;
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = this.window.URL.createObjectURL(blob);
    const link = this.window.document.createElement('a');
    link.href = url;
    link.download = 'Adnuntius-Tracked-Keywords.csv';
    this.window.document.body.appendChild(link);
    link.click();
    this.window.document.body.removeChild(link);
    this.window.URL.revokeObjectURL(url);
  }

  uploadHook(): void {
    this.isLoading.set(true);
    this.contextApi.get(this.editingModel.id).subscribe({
      next: (data) => {
        this.ngZone.run(() => {
          if (data) {
            this.saveDirect(data);
          }
          this.isLoading.set(false);
        });
      },
      error: () => {
        this.ngZone.run(() => this.isLoading.set(false));
      }
    });
  }

  private getRows(): any[] {
    return _.map(this.vm.trackedKeywords || [], (trackedKeyword) => this.sanitiseVmTrackedKeywords(trackedKeyword));
  }

  private sanitiseVmTrackedKeywords(trackedKeyword: any): any {
    const mapping = (trackedKeyword.mappings || '').match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g);
    return {
      keyword: trackedKeyword.keyword,
      mappings: mapping ? mapping.map((entry: string) => entry.trim()) : []
    };
  }

  private hydrateVm(model: ContextServiceConfigurationModel): void {
    model.contextService = model.contextService || 'ADNUNTIUS';

    this.vm = this.vm || {};
    this.vm.keywordMetaTags = _.get(model, ['crawlerConfig', 'keywordMetaTags'], []).join(', ');
    this.vm.categoryMetaTags = _.get(model, ['crawlerConfig', 'categoryMetaTags'], []).join(', ');
    this.vm.deriveKeywordsFromUrl = _.get(model, ['crawlerConfig', 'deriveKeywordsFromUrl'], false);
    this.vm.categoryKeywords = [];
    _.forEach(_.get(model, ['crawlerConfig', 'categoryKeywords']), (val, key) => {
      this.vm.categoryKeywords.push({category: key, keywords: (val || []).join(', ')});
    });
    this.vm.categoryKeywords.sort((a: any, b: any) => {
      const aEntry = IAB_TAXONOMY.find((entry) => entry.id === a.category);
      const bEntry = IAB_TAXONOMY.find((entry) => entry.id === b.category);
      return (aEntry && aEntry.order) > (bEntry && bEntry.order) ? 1 : -1;
    });
    this.vm.selectorConfiguration = [];
    _.forEach(_.get(model, ['crawlerConfig', 'selectorConfiguration']), (val, key) => {
      this.vm.selectorConfiguration.push({domain: key, value: (val || {})});
    });
    this.vm.selectorConfiguration.sort((a: any, b: any) => String(a.domain).localeCompare(String(b.domain)));
    this.vm.trackedKeywords = (_.get(model, ['crawlerConfig', 'trackedKeywords'], []) || []).map((trackedKeyword: any) => {
      return {
        keyword: trackedKeyword.keyword,
        mappings: (trackedKeyword.mappings || []).map((mapping: string) => {
          if (mapping.includes(',') && !mapping.startsWith('"')) {
            return `"${mapping}"`;
          }
          return mapping;
        }).join(', ')
      };
    });
  }
}
